/**
 * A casca do U1.
 *
 * Junta as quatro peças que os outros módulos não conhecem entre si: o domo
 * (expressão), o painel (modo de voz e saúde), a máquina de estados e o stream
 * de eventos. Nenhuma regra de conversa mora aqui — este arquivo é fiação.
 *
 * A ordem importa: a sessão nasce antes do stream (o WebSocket precisa do
 * ticket), e o stream nasce antes de qualquer envio — a fila local do
 * `EventClient` cobre a janela entre um e outro.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
} from 'react';
import { DEFAULT_VOICE_MODE, type U1Event, type VoiceMode } from '@will/contracts';
import { authFetch, IdentidadeAtual } from './auth';
import { Dome } from './dome';
import { Panel } from './panel';
import { Agenda, Conectores, Tarefas } from './painel/Colunas';
import { Engrenagem, Gaveta } from './painel/Gaveta';
import { usePainel } from './painel/usePainel';
import { escolhaAtual } from './voice/escolha';
import { createVoiceEngine, type VoiceEngine } from './voice';
import { useConversation } from './state/useConversation';
import { publicarNivelAudio, useNivelAudio } from './state/audioLevel';
import { EventClient, type ConnectionStatus, type Unsubscribe } from './client/eventClient';
import { clearSession, createSession, SessionError, renovarTicket } from './client/session';
import { telemetriaDaSessao } from './client/telemetry';
import { mensagemDeErro, textoDaTranscricao } from './client/payloads';
import './App.css';

const ROTULO_CONEXAO: Record<ConnectionStatus, string> = {
  idle: 'Iniciando',
  connecting: 'Conectando',
  open: 'Conectado',
  reconnecting: 'Reconectando',
  closed: 'Desconectado',
};

/**
 * Piso de animação do domo quando não há áudio medido.
 *
 * O nível verdadeiro vem do módulo de voz por `publicarNivelAudio`; com o
 * microfone desligado (ou no modo texto) o domo ficaria parado, o que se lê
 * como travado e não como quieto. O piso varia por estado porque um Will
 * pensando não deve pulsar como um Will falando.
 */
function pisoDeNivel(falando: boolean, estado: string): number {
  if (falando) return 0.34;
  if (estado === 'USER_SPEAKING') return 0.28;
  if (estado === 'THINKING' || estado === 'TOOL_RUNNING') return 0.14;
  return 0.06;
}

/**
 * Um identificador de correlação para este turno.
 *
 * `crypto.randomUUID` só existe em contexto seguro — em `http://` de
 * desenvolvimento ele some, e um turno não pode falhar por causa disso. A
 * reserva não precisa ser criptográfica: isto correlaciona registros, não
 * autentica nada.
 */
function novoTrace(): string {
  const c = globalThis.crypto;
  if (typeof c?.randomUUID === 'function') return c.randomUUID().replace(/-/g, '');
  return Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
}

/**
 * Os conectores que a pessoa deixou ligados, entre visitas.
 *
 * `null` (nada salvo) é diferente de lista vazia, e a diferença é o produto
 * inteiro: quem nunca escolheu começa com tudo que pode usar ligado, e quem
 * desligou todos continua com todos desligados. Guardar só a lista faria a
 * segunda pessoa reencontrar tudo ligado na volta.
 */
const CHAVE_CONECTORES = 'will.u1.conectores';

function lerConectoresSalvos(): string[] | null {
  try {
    const cru = window.localStorage.getItem(CHAVE_CONECTORES);
    if (cru === null) return null;
    const d: unknown = JSON.parse(cru);
    return Array.isArray(d) ? d.filter((v): v is string => typeof v === 'string') : null;
  } catch {
    // localStorage bloqueado: a escolha vira volátil. Perder a memória é
    // aceitável; quebrar a tela por causa dela, não.
    return null;
  }
}

function salvarConectores(ids: readonly string[]): void {
  try {
    window.localStorage.setItem(CHAVE_CONECTORES, JSON.stringify(ids));
  } catch {
    /* ver acima */
  }
}

export default function App(): JSX.Element {
  const conversa = useConversation();
  const nivelMedido = useNivelAudio();

  const [modoVoz, setModoVoz] = useState<VoiceMode>(DEFAULT_VOICE_MODE);
  const [conexao, setConexao] = useState<ConnectionStatus>('idle');
  const [ouvido, setOuvido] = useState('');
  const [parcial, setParcial] = useState('');
  const [resposta, setResposta] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [rascunho, setRascunho] = useState('');
  const [microfone, setMicrofone] = useState(false);
  /** incrementar força um novo bootstrap — é o que o botão "reiniciar" faz */
  const [geracao, setGeracao] = useState(0);

  const clienteRef = useRef<EventClient | null>(null);
  const vozRef = useRef<VoiceEngine | null>(null);

  /**
   * Os handlers do stream são registrados uma vez, no bootstrap. Se lessem
   * `conversa` pelo closure ficariam presos ao primeiro render e aplicariam
   * transições a partir de um estado obsoleto. Os refs são atualizados a cada
   * render e os handlers leem sempre a versão de agora.
   */
  const conversaRef = useRef(conversa);
  conversaRef.current = conversa;
  const respostaRef = useRef(resposta);
  respostaRef.current = resposta;
  /** Acúmulo síncrono do que o Will vai falar. Não depende de render. */
  /**
   * A aprovação pendente, se houver.
   *
   * Sem isto o Will pedia autorização para escrever e a interface não oferecia
   * como responder: só sobrava dizer "sim" no turno seguinte, sem nada na tela
   * indicando que havia algo esperando. A sessão ficava presa em
   * WAITING_APPROVAL e a ação nunca acontecia — nem confirmada, nem recusada.
   */
  const [aprovacao, setAprovacao] = useState<{
    id: string;
    resumo: string;
    /** o nome exato do que vai rodar; `null` quando o servidor não disse */
    ferramenta: string | null;
    /** `low` · `medium` · `high` · `critical`, como o registro classifica */
    risco: string | null;
    /** instante em que a aprovação vence; `null` quando não veio */
    expiraEm: number | null;
  } | null>(null);
  /**
   * Segundos que faltam para a aprovação vencer.
   *
   * Existe porque a aprovação tem prazo de 120 s e nada na tela dizia isso: a
   * pessoa lia, pensava, clicava, e recebia uma recusa sem entender por quê. Um
   * prazo que só aparece quando estoura é um prazo que ensina desconfiança.
   */
  const [segundosParaVencer, setSegundosParaVencer] = useState<number | null>(null);

  // A contagem regressiva do cartão de aprovação.
  //
  // Um tique por segundo, e só enquanto há cartão na tela — um temporizador que
  // sobrevive ao cartão é o tipo de coisa que fica rodando esquecida.
  //
  // Ele NÃO decide nada: quem recusa uma aprovação vencida é o servidor, que é
  // quem tem o relógio que vale. Isto aqui informa; se o navegador estiver com
  // a hora errada, a contagem fica errada e a decisão continua certa.
  useEffect(() => {
    if (aprovacao?.expiraEm == null) {
      setSegundosParaVencer(null);
      return;
    }
    const alvo = aprovacao.expiraEm;
    const calcular = (): void =>
      setSegundosParaVencer(Math.max(0, Math.round((alvo - Date.now()) / 1000)));
    calcular();
    const id = window.setInterval(calcular, 1000);
    return () => window.clearInterval(id);
  }, [aprovacao?.expiraEm]);

  const falaRef = useRef('');
  /**
   * O perfil de prosódia do turno em curso.
   *
   * Chega no `speech_started` e é usado no `speech_ended`, que é quando o
   * OmniVoice de fato sintetiza. Vive num ref pela mesma razão que `falaRef`:
   * os dois eventos podem cair no mesmo tique, e um valor de closure estaria
   * velho. Não é o rosto do domo — durante a fala o rosto é `speaking`; isto é
   * a emoção que o cérebro escolheu para o que está sendo dito.
   */
  const prosodiaRef = useRef<string | undefined>(undefined);
  const modoVozRef = useRef(modoVoz);
  modoVozRef.current = modoVoz;

  // -- ações ----------------------------------------------------------------

  const enviarTurno = useCallback((texto: string) => {
    setResposta('');
    setErro(null);
    // A rota é declarada, não adivinhada: o contrato não liga LISTENING a
    // THINKING diretamente, e um turno digitado ainda é um turno do usuário.
    if (conversaRef.current.estado === 'LISTENING') {
      conversaRef.current.percorrer(['USER_SPEAKING', 'THINKING'], 'turno do usuário');
    }
    // O trace nasce AQUI, no clique, e não no servidor: o que interessa medir
    // começa antes de o quadro subir, e sem um id comum a parte do navegador
    // não se liga ao resto. O servidor gera um quando falta, e recusa formato
    // que não seja hexadecimal — este valor acaba em log e em trilha de
    // auditoria, e id de origem externa sem validação nesses dois lugares é
    // injeção esperando acontecer.
    clienteRef.current?.send({
      type: 'u1.client.text_turn',
      text: texto,
      trace_id: novoTrace(),
    });
  }, []);

  // -- bootstrap: sessão + stream -------------------------------------------

  useEffect(() => {
    let vivo = true;
    let cliente: EventClient | null = null;
    let cancelar: Unsubscribe[] = [];

    conversaRef.current.transicionar('CONNECTING', 'bootstrap');

    createSession({ voice_mode: modoVozRef.current })
      .then((sessao) => {
        if (!vivo) return;
        setModoVoz(sessao.voice_mode ?? DEFAULT_VOICE_MODE);

        telemetriaDaSessao(sessao.session_id);
        cliente = new EventClient({
          sessionId: sessao.session_id,
          ticket: sessao.ticket,
          // A saída do laço de reconexão: credencial vencida se compra de
          // novo pelo token de acesso, que dura muito mais que o ticket.
          renovar: renovarTicket,
          onSessaoTrocada: () => {
            // A conversa anterior acabou junto com a sessão. Voltar a máquina
            // para IDLE é mais honesto que seguir mostrando o estado de uma
            // sessão que não existe mais.
            conversaRef.current.reiniciar('a sessão anterior expirou');
          },
          // O runtime responde `u1.control.accepted` com `reason: 'passo'`
          // sempre que uma mensagem desta tela mexeu num passo — executado,
          // marcado como feito ou dispensado. É o único aviso que chega: nada
          // disso publica `u1.tool.completed`, então sem esta linha o painel
          // continuava mostrando o plano de antes do clique, e o botão ficava
          // em "Executando…" até alguém recarregar a página.
          //
          // Recarrega em vez de escrever o novo estado aqui: o servidor
          // mandou junto o que valeu, mas manter uma segunda cópia do estado
          // do passo na tela é como as duas verdades começam. Uma palavra só
          // ('passo') para as três respostas, de propósito — para o cliente
          // todas significam "o plano mudou, releia".
          onControl: (tipo, quadro) => {
            if (tipo === 'u1.control.accepted' && quadro['reason'] === 'passo') {
              setGatilhoDoPainel((n) => n + 1);
              return;
            }
            // Recusa apaga as marcas locais de "pedi e ainda não sei".
            //
            // O quadro de recusa não diz de qual passo ele fala, então não dá
            // para tirar só um — e deixá-las todas seria pior: o botão fica em
            // "Executando…" ou "Dispensando…" para sempre, sobre um pedido que
            // já teve resposta, e a pessoa lê como travamento. As marcas só
            // existem para atravessar a espera; sem espera, não têm o que
            // fazer. Quem protege de verdade contra a segunda execução é o
            // servidor, no UPDATE condicional que reivindica o passo — nunca
            // foi este conjunto.
            // Antes, QUALQUER recusa limpava as marcas. Era inofensivo enquanto
            // passo era a única coisa recusável, e deixou de ser quando o
            // servidor passou a recusar proposta de estado: uma recusa dessas
            // apagaria os botões de passo sem ter nada a ver com eles, e a
            // pessoa veria a interface esquecer o que acabou de pedir.
            //
            // A lista é do que **sabidamente não é passo**, e não do que é. As
            // recusas de passo não compartilham prefixo — `papel_insuficiente`
            // e `aprovacao_pendente` são recusas de passo tanto quanto
            // `passo_encerrado` —, então filtrar por `startsWith('passo')`
            // deixaria o botão travado justo nos casos em que a pessoa mais
            // precisa entender o que houve.
            //
            // Isto é remendo, e está dito: o conserto de verdade é o servidor
            // dizer a QUE ASSUNTO a recusa pertence, em vez de o cliente
            // deduzir do código. Enquanto o assunto não existir no contrato,
            // errar para o lado de limpar é o lado certo — marca presa é botão
            // morto; marca limpa a mais é um clique a mais.
            const NAO_E_PASSO = new Set([
              'estado_desconhecido', 'transicao_invalida', 'porta_errada',
              'origem_errada', 'estado_do_servidor', 'voice_mode_desconhecido',
              'privacy_invalido', 'turno_vazio',
            ]);
            if (tipo === 'u1.control.rejected' && !NAO_E_PASSO.has(String(quadro['reason'] ?? ''))) {
              setPassosDisparados((atual) => (atual.size === 0 ? atual : new Set()));
              setPassosDispensando((atual) => (atual.size === 0 ? atual : new Set()));
            }
          },
        });
        clienteRef.current = cliente;

        cancelar = [
          cliente.onStatus(setConexao),

          cliente.subscribe('u1.user.transcript_delta', (e: U1Event) => {
            setParcial((antes) => antes + textoDaTranscricao(e.payload));
          }),
          cliente.subscribe('u1.user.transcript_final', (e: U1Event) => {
            const texto = textoDaTranscricao(e.payload);
            setParcial('');
            if (texto) setOuvido(texto);
            // Final vazio é o caso do 'não entendi': houve som, não houve turno.
            else conversaRef.current.expressar('unclear', 2_500);
          }),
          cliente.subscribe('u1.will.speech_started', (e: U1Event) => {
            falaRef.current = '';
            const sugerido = (e.payload as { dome?: unknown } | null)?.dome;
            prosodiaRef.current = typeof sugerido === 'string' ? sugerido : undefined;
            setResposta('');
          }),
          cliente.subscribe('u1.will.transcript_delta', (e: U1Event) => {
            const pedaco = textoDaTranscricao(e.payload);
            // O acúmulo é SÍNCRONO, num ref, e o estado serve só para a tela.
            // O servidor emite transcript_delta e speech_ended no mesmo tique;
            // um ref sincronizado durante o render ainda guardava o valor
            // anterior quando o speech_ended chegava, e o Will falava string
            // vazia. Intermitente, porque dependia de o React ter renderizado
            // entre os dois eventos.
            falaRef.current += pedaco;
            setResposta((antes) => antes + pedaco);
          }),
          cliente.subscribe('u1.will.speech_ended', () => {
            // TODO(voz): em `hybrid` quem fala é o OmniVoice, e isso acontece no
            // cliente. Falar só depois do texto inteiro é correto mas não é o
            // mais rápido; quando o runtime marcar fim de frase, trocar por
            // fala por sentença. O ADR-007 já contabiliza essa latência.
            const texto = falaRef.current.trim();
            if (!texto) {
              console.warn('[u1] speech_ended sem texto acumulado — nada a falar');
              return;
            }
            if (modoVozRef.current === 'text') return;
            const voz = vozRef.current;
            if (!voz) {
              console.warn('[u1] speech_ended sem engine de voz — o Will ficaria mudo');
              return;
            }
            // O rosto no instante da fala escolhe o perfil de prosódia. Vem do
            // ref e não do render: `speech_ended` chega no mesmo tique que o
            // último `transcript_delta`, e o valor do closure estaria velho.
            voz.speak(texto, prosodiaRef.current).catch((causa: unknown) => {
              console.warn('[u1] falha ao falar:', causa);
            });
          }),
          cliente.subscribe('u1.approval.required', (e: U1Event) => {
            const p = e.payload as {
              approval_id?: unknown;
              action_summary?: unknown;
              tool_name?: unknown;
              risk?: unknown;
              expires_at?: unknown;
            };
            if (typeof p.approval_id !== 'string') return;
            // `tool_name`, `risk` e `expires_at` eram publicados pelo servidor e
            // descartados aqui — a décima sexta vez que uma ponta expõe e a
            // outra não consome. A consequência era específica e ruim: quem
            // aprovava não via **o que** ia rodar, **quanto risco** tinha, nem
            // que a decisão tem **prazo**. Consentimento sem esses três é
            // consentimento sobre uma frase, não sobre uma ação.
            setAprovacao({
              id: p.approval_id,
              resumo:
                typeof p.action_summary === 'string' ? p.action_summary : 'executar esta ação',
              ferramenta: typeof p.tool_name === 'string' ? p.tool_name : null,
              risco: typeof p.risk === 'string' ? p.risk : null,
              // Guardado como instante, não como texto: o cartão conta o tempo
              // que falta, e um rótulo fixo ("expira em 120s") mentiria assim
              // que a pessoa parasse para ler.
              expiraEm: typeof p.expires_at === 'string' ? Date.parse(p.expires_at) : null,
            });
          }),
          // Concedida, recusada ou vencida: em todos os casos não há mais o que
          // decidir, e deixar o cartão na tela convidaria a um segundo clique
          // sobre uma ação que já teve desfecho.
          // Ferramenta executada: agenda e tarefas podem ter mudado. É o único
          // momento em que elas mudam por dentro deste produto, e recarregar
          // aqui evita tanto o intervalo fixo (que pisca à toa) quanto a lista
          // desatualizada logo depois de a pessoa criar algo falando.
          cliente.subscribe('u1.tool.started', (e: U1Event) => {
            const nome = String((e.payload as { tool_name?: string }).tool_name ?? '');
            const prefixo = nome.split('.')[0] ?? '';
            if (prefixo === '') return;
            setConectorEmUso(prefixo);
            window.setTimeout(() => setConectorEmUso((atual) => (atual === prefixo ? null : atual)), 1600);
          }),
          cliente.subscribe('u1.tool.completed', () => setGatilhoDoPainel((n) => n + 1)),
          cliente.subscribe('u1.tool.partial', () => setGatilhoDoPainel((n) => n + 1)),
          cliente.subscribe('u1.approval.granted', () => setAprovacao(null)),
          cliente.subscribe('u1.approval.rejected', () => setAprovacao(null)),
          cliente.subscribe('u1.approval.expired', () => setAprovacao(null)),
          cliente.subscribe('u1.error', (e: U1Event) => {
            setErro(mensagemDeErro(e.payload));
          }),
          cliente.subscribe('u1.session.ended', () => {
            // Fim normal de sessão não é erro, e o contrato só chega em IDLE
            // vindo de ERROR — por isso a escotilha explícita da máquina.
            conversaRef.current.reiniciar('sessão encerrada pelo runtime');
          }),

          // Por último: os handlers de tipo já ajustaram o texto, e agora a
          // máquina move o estado. A ordem evita um quadro em que a expressão
          // do domo e o texto na tela discordam.
          cliente.subscribeAll((e: U1Event) => {
            conversaRef.current.aplicarEvento(e);
          }),
        ];

        // Conectar é o último passo, e é explícito: o construtor do EventClient
        // não abre socket. Sem esta linha o cliente ficava montado e assinado,
        // o Presence respondia, e a resposta não tinha por onde voltar — o
        // navegador nunca tentava abrir o stream (zero linhas no log do nginx).
        // Depois das assinaturas de propósito: assim nenhum evento cai no vão
        // entre o socket abrir e os assinantes existirem.
        cliente.connect();
      })
      .catch((causa: unknown) => {
        if (!vivo) return;
        setErro(
          causa instanceof SessionError
            ? causa.message
            : `não foi possível iniciar a sessão: ${String(causa)}`,
        );
        conversaRef.current.transicionar('ERROR', 'bootstrap falhou');
      });

    return () => {
      vivo = false;
      for (const cancelarInscricao of cancelar) cancelarInscricao();
      cancelar = [];
      cliente?.close();
      clienteRef.current = null;
    };
  }, [geracao]);

  // -- voz: o engine existe sempre; o microfone só liga a escuta -------------
  //
  // Eram um efeito só, guardado por `if (!microfone) return`. Consequência: sem
  // clicar em "Ouvir" não havia engine, `vozRef` ficava nulo e o `speak()` do
  // `speech_ended` não fazia nada — o Will escrevia e nunca falava. Falar não
  // pede permissão do navegador; só ouvir pede. São dois efeitos.

  useEffect(() => {
    const engine = createVoiceEngine(modoVoz, {
      onBargeIn: () => {
        clienteRef.current?.send({ type: 'u1.client.interrupt' });
      },
      onError: (causa: Error) => setErro(causa.message),
      onFallback: (motivo: Error) => {
        // Queda para a escuta do navegador é degradação, não falha: o usuário
        // continua sendo ouvido, com qualidade menor. Não vira erro na tela.
        console.warn('[u1] escuta caiu para o navegador:', motivo.message);
      },
    });
    vozRef.current = engine;

    engine.onLevel(publicarNivelAudio);
    engine.onHeard((texto: string, final: boolean) => {
      if (!final) {
        setParcial(texto);
        return;
      }
      setParcial('');
      if (!texto.trim()) return;
      setOuvido(texto);
      enviarTurno(texto);
    });

    return () => {
      engine.stop();
      vozRef.current = null;
      publicarNivelAudio(0);
    };
  }, [enviarTurno, modoVoz, publicarNivelAudio]);

  useEffect(() => {
    const engine = vozRef.current;
    if (!engine) return undefined;

    if (!microfone) {
      // Para de ouvir e mantém a voz: desligar o microfone é escolha sobre
      // escutar, não sobre responder.
      engine.stop();
      publicarNivelAudio(0);
      return undefined;
    }

    let vivo = true;
    engine.start().catch((causa: unknown) => {
      if (!vivo) return;
      setErro(causa instanceof Error ? causa.message : String(causa));
      setMicrofone(false);
    });

    return () => {
      vivo = false;
    };
  }, [microfone, modoVoz, publicarNivelAudio]);

  const aoEnviarFormulario = useCallback(
    (evento: FormEvent<HTMLFormElement>) => {
      evento.preventDefault();
      const texto = rascunho.trim();
      if (!texto) return;
      setOuvido(texto);
      setParcial('');
      setRascunho('');
      enviarTurno(texto);
    },
    [enviarTurno, rascunho],
  );

  const aoTrocarModo = useCallback((modo: VoiceMode) => {
    setModoVoz(modo);
    // ADR-007: trocar de modo exige reconfigurar a sessão Realtime e só vale a
    // partir do próximo turno. Avisamos o runtime; o efeito do microfone
    // reconstrói o engine local por conta da mudança de dependência.
    clienteRef.current?.send({ type: 'u1.client.voice_mode', mode: modo });
  }, []);

  const reiniciarSessao = useCallback(() => {
    clienteRef.current?.close();
    clearSession();
    setErro(null);
    setOuvido('');
    setParcial('');
    setResposta('');
    conversaRef.current.reiniciar('reinício solicitado pelo usuário');
    setGeracao((n) => n + 1);
  }, []);

  const decidirAprovacao = useCallback((concedida: boolean) => {
    setAprovacao((pendente) => {
      if (pendente !== null) {
        clienteRef.current?.send({
          type: 'u1.client.approval',
          approval_id: pendente.id,
          granted: concedida,
        });
      }
      // Sai da tela na hora: a confirmação chega por evento, e manter o cartão
      // até lá convida ao segundo clique — que o `action_hash` recusaria, mas
      // que a pessoa leria como travamento.
      return null;
    });
  }, []);

  const interromper = useCallback(() => {
    vozRef.current?.hush();
    clienteRef.current?.send({ type: 'u1.client.interrupt' });
  }, []);

  // -- colunas, gaveta e conectores ------------------------------------------

  const [gaveta, setGaveta] = useState(false);
  // Recarrega as colunas quando o Will de fato mexeu em alguma coisa. Sem isto
  // a pessoa cria uma tarefa falando e a lista ao lado continua desatualizada,
  // que é a maneira mais rápida de ela deixar de confiar na lista.
  const [gatilhoDoPainel, setGatilhoDoPainel] = useState(0);
  const { painel, erro: erroDoPainel } = usePainel(gatilhoDoPainel);

  const [conectoresLigados, setConectoresLigados] = useState<ReadonlySet<string>>(
    () => new Set(lerConectoresSalvos()),
  );

  // A escolha vale para o servidor, não só para a tela: é ela que sai do
  // catálogo mandado ao cérebro. Reenviada a cada (re)conexão porque ela vive
  // na conexão — um socket novo não sabe do que foi escolhido no anterior.
  useEffect(() => {
    if (conexao !== 'open') return;
    clienteRef.current?.send({
      type: 'u1.client.connectors',
      enabled: [...conectoresLigados],
    });
  }, [conexao, conectoresLigados]);

  /**
   * Quais tarefas estão com o plano aberto.
   *
   * Mora aqui, e não no cartão, porque o cartão é remontado a cada carga do
   * painel — a `key` muda de propósito. Dentro dele, a escolha de abrir morreria
   * junto com o componente, e a lista fecharia sozinha logo depois de a pessoa
   * apertar "Executar": o passo executa, o painel recarrega, e o que ela estava
   * olhando some. Abrir é escolha de quem usa; recarregar dado não é motivo para
   * desfazê-la.
   */
  const [passosAbertos, setPassosAbertos] = useState<ReadonlySet<string>>(() => new Set());

  const alternarPassos = useCallback((tarefaId: string) => {
    setPassosAbertos((atual) => {
      const proximo = new Set(atual);
      if (!proximo.delete(tarefaId)) proximo.add(tarefaId);
      return proximo;
    });
  }, []);

  /**
   * Passos cujo pedido já subiu e cuja resposta ainda não voltou.
   *
   * Entre o clique e o servidor dizer `executando` existe uma janela — curta,
   * mas suficiente para um segundo clique virar uma segunda execução do mesmo
   * passo. Aqui a tela já sabe o que pediu, e o botão fica desabilitado desde o
   * primeiro clique em vez de desde a resposta.
   */
  const [passosDisparados, setPassosDisparados] = useState<ReadonlySet<string>>(() => new Set());

  const executarPasso = useCallback((passoId: string) => {
    // O envio fica FORA do updater: em StrictMode o React chama o updater duas
    // vezes por render, e um `send` lá dentro mandaria o passo duas vezes em
    // desenvolvimento. Contra o clique duplo quem protege é o `disabled`.
    clienteRef.current?.send({ type: 'u1.client.step_run', step_id: passoId });
    setPassosDisparados((atual) => new Set(atual).add(passoId));
  }, []);

  /**
   * Passos cuja dispensa já subiu e cuja resposta ainda não voltou.
   *
   * Separado de `passosDisparados` porque os dois botões vivem em linhas
   * diferentes: um passo que falhou pode receber dispensa e não pode receber
   * execução, e um conjunto só faria a lista oferecer "Executando…" em cima
   * dele. Ver o comentário do `dispensando` em `painel/Passos.tsx`.
   */
  const [passosDispensando, setPassosDispensando] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  const dispensarPasso = useCallback((passoId: string) => {
    // Mesma regra do irmão acima: o `send` fora do updater, porque em
    // StrictMode o updater roda duas vezes e a mensagem sairia dobrada. A
    // segunda seria recusada pelo servidor ('passo_encerrado'), o que é a
    // defesa que importa — esta aqui só evita o barulho.
    clienteRef.current?.send({ type: 'u1.client.step_dismiss', step_id: passoId });
    setPassosDispensando((atual) => new Set(atual).add(passoId));
  }, []);

  // A marca local vale até o servidor assumir o passo. Depois disso ela seria
  // uma segunda verdade sobre o mesmo estado — e a do banco é a que manda.
  useEffect(() => {
    setPassosDisparados((atual) => {
      if (atual.size === 0) return atual;
      const restam = [...atual].filter((id) =>
        painel.passos.some((p) => p.id === id && p.estado === 'pendente'),
      );
      return restam.length === atual.size ? atual : new Set(restam);
    });
  }, [painel.passos]);

  // O mesmo para a dispensa, com a única diferença que importa: aqui o que
  // ainda espera resposta é o passo que continua ABERTO. Uma dispensa pedida
  // sobre um passo que falhou some da marca assim que o painel volta dizendo
  // `dispensado` — e some também se o servidor tiver recusado, porque nesse
  // caso o estado não mudou e a linha volta a oferecer o botão. Preso para
  // sempre em "Dispensando…" seria o pior dos dois.
  useEffect(() => {
    setPassosDispensando((atual) => {
      if (atual.size === 0) return atual;
      const restam = [...atual].filter((id) =>
        painel.passos.some(
          (p) => p.id === id && (p.estado === 'pendente' || p.estado === 'falhou'),
        ),
      );
      return restam.length === atual.size ? atual : new Set(restam);
    });
  }, [painel.passos]);

  const alternarConector = useCallback((id: string, ligar: boolean) => {
    setConectoresLigados((atual) => {
      const proximo = new Set(atual);
      if (ligar) proximo.add(id);
      else proximo.delete(id);
      salvarConectores([...proximo]);
      return proximo;
    });
  }, []);

  // Na primeira carga, quem nunca escolheu fica com tudo que pode usar ligado —
  // o comportamento de antes desta tela. `null` salvo seria ambíguo com "estão
  // todos desligados", então a diferença mora no armazenamento, não aqui.
  useEffect(() => {
    if (lerConectoresSalvos() !== null) return;
    const disponiveis = painel.conectores.filter((c) => c.estado === 'ligado').map((c) => c.id);
    if (disponiveis.length === 0) return;
    setConectoresLigados(new Set(disponiveis));
  }, [painel.conectores]);

  const [tocando, setTocando] = useState<'ouvido' | 'resposta' | null>(null);

  /**
   * Qual conector o Will acabou de tocar.
   *
   * Não é enfeite: hoje uma ferramenta roda em 5 ms e some sem deixar rastro na
   * tela, então a pessoa vê o Will responder sobre a agenda sem nunca ver a
   * agenda ser consultada. O satélite pulsando é a diferença entre "ele disse"
   * e "ele foi lá". Dura o suficiente para o olho pegar e some sozinho — um
   * estado que fica aceso vira decoração e deixa de significar "agora".
   */
  const [conectorEmUso, setConectorEmUso] = useState<string | null>(null);

  /**
   * Toca um texto em voz alta, com a voz e o modo escolhidos na bancada.
   *
   * Existe para os dois botões de ouvir: reler é o que se faz quando não se tem
   * certeza do que foi transcrito, e reouvir a resposta é o que se faz quando
   * ela passou enquanto a pessoa olhava para outro lado. Sem isto, a única
   * forma de rever era pedir de novo — o que gasta um turno e produz outra
   * resposta, não a mesma.
   */
  const ouvirTexto = useCallback(async (qual: 'ouvido' | 'resposta', texto: string) => {
    const limpo = texto.trim();
    if (limpo === '') return;
    setTocando(qual);
    try {
      const escolha = escolhaAtual();
      // `authFetch` e não `fetch`: sintetizar voz gasta a conta de provedor do
      // titular, e o proxy passou a exigir sessão do U1 nessa rota. A
      // credencial vem do único lugar que a guarda, e o 401 já leva renovação
      // e uma repetição junto.
      const r = await authFetch('/api/tts', {
        method: 'POST',
        headers: { 'content-type': 'application/json', accept: 'audio/ogg' },
        body: JSON.stringify({
          text: limpo,
          ...(escolha.vozId === null ? {} : { voice_id: escolha.vozId }),
          ...(escolha.modo === null ? {} : { mode: escolha.modo }),
          ...(escolha.idioma === null ? {} : { language: escolha.idioma }),
        }),
      });
      if (!r.ok) throw new Error(String(r.status));
      const url = URL.createObjectURL(await r.blob());
      const audio = new Audio(url);
      const soltar = (): void => {
        URL.revokeObjectURL(url);
        setTocando(null);
      };
      audio.onended = soltar;
      audio.onerror = soltar;
      await audio.play();
    } catch {
      // Falhar aqui não merece alarme: o texto está na tela, e é ele que
      // importa. Um erro vermelho por não conseguir reler seria mais ruído que
      // informação.
      setTocando(null);
    }
  }, []);

  // -- render ----------------------------------------------------------------

  const nivel = Math.max(nivelMedido, pisoDeNivel(conversa.falando, conversa.estado));

  // Pensar inclui preparar e executar ferramenta: para quem espera, os três são
  // a mesma coisa — o Will está trabalhando e ainda não falou.
  const pensando =
    conversa.estado === 'THINKING' ||
    conversa.estado === 'PREAMBLE' ||
    conversa.estado === 'TOOL_RUNNING';

  // A cor da expressão é dado do contrato, não do CSS: entra como variável para
  // a casca acompanhar o domo sem duplicar a paleta em folha de estilo.
  const estiloAcento = useMemo(
    () => ({ '--u1-acento': conversa.expressao.color }) as CSSProperties,
    [conversa.expressao.color],
  );

  return (
    <div className="u1" style={estiloAcento}>
      <header className="u1-topo">
        <span className="u1-marca">Will</span>
        {/* Quem está falando, e de qual empresa, resolvido pelo servidor. Fica
            no cabeçalho porque tenant errado na tela precisa ser visível o
            tempo todo, não só quando alguém abre um menu. */}
        <IdentidadeAtual />
        <span className={`u1-conexao u1-conexao--${conexao}`}>
          <i className="u1-ponto" aria-hidden="true" />
          {ROTULO_CONEXAO[conexao]}
        </span>
        {/* Uma engrenagem, e só. Tudo que é configuração mora atrás dela: são
            escolhas que se fazem uma vez por semana, e estavam ocupando uma
            coluna inteira todos os dias. */}
        <button
          type="button"
          className={`u1-engrenagem${gaveta ? ' u1-engrenagem--on' : ''}`}
          onClick={() => setGaveta(true)}
          aria-haspopup="dialog"
          aria-expanded={gaveta}
        >
          <Engrenagem />
          <span className="u1-oculto">Configuração</span>
        </button>
      </header>

      <main className="u1-palco">
        <aside className="u1-lado u1-lado--esq" aria-label="Agenda e tarefas">
          {/* A `key` muda quando o painel recarrega, e isso reencena a
              animação de entrada do cartão. É o jeito mais barato de mostrar
              "isto acabou de mudar" sem comparar listas: depois de uma
              ferramenta rodar, a coluna pisca e o olho vai para lá. */}
          <Agenda
            key={`agenda-${gatilhoDoPainel}`}
            eventos={painel.agenda}
            falhou={painel.falhas.includes('agenda')}
          />
          <Tarefas
            key={`tarefas-${gatilhoDoPainel}`}
            tarefas={painel.tarefas}
            passos={painel.passos}
            falhou={painel.falhas.includes('tarefas')}
            passosFalharam={painel.falhas.includes('passos')}
            abertas={passosAbertos}
            onAlternar={alternarPassos}
            disparados={passosDisparados}
            dispensando={passosDispensando}
            onExecutar={executarPasso}
            onDispensar={dispensarPasso}
          />
          {erroDoPainel !== null && (
            <p className="u1-lado__aviso" role="status">
              Estes dados podem estar velhos: {erroDoPainel}.
            </p>
          )}
        </aside>

        <section className="u1-centro">
          <div className="u1-domo">
            {/* Os satélites da referência, com um significado: cada um é um
                conector que o Will alcança AGORA. Clicar desliga, igual ao
                interruptor da coluna — não são enfeite orbital, são o mesmo
                controle mais perto do olho. Aparecem só os disponíveis: dois
                satélites honestos valem mais que quatro decorativos. */}
            <div className="u1-orbita">
              <Dome state={conversa.dome} level={nivel} talking={conversa.falando} />
              <ul className="u1-satelites">
                {painel.conectores
                  .filter((c) => c.estado === 'ligado')
                  .slice(0, 4)
                  .map((c, i) => {
                    const ativo = conectoresLigados.has(c.id);
                    return (
                      <li
                        key={c.id}
                        className={`u1-satelite u1-satelite--${i}${conectorEmUso === c.id ? ' u1-satelite--usando' : ''}`}
                      >
                        <button
                          type="button"
                          className={`u1-satelite__botao${ativo ? ' u1-satelite__botao--on' : ''}`}
                          onClick={() => alternarConector(c.id, !ativo)}
                          aria-pressed={ativo}
                        >
                          {c.nome}
                        </button>
                      </li>
                    );
                  })}
              </ul>
            </div>
            <h1 className="u1-saudacao">{conversa.expressao.greeting}</h1>
            <p className="u1-legenda">{conversa.expressao.sub}</p>
          </div>

          <div className="u1-chat">
            <article className="u1-bloco">
              <header className="u1-bloco__topo">
                <h2 className="u1-rotulo">Ouvido</h2>
                {/* Reler não é luxo: transcrição é probabilística, e conferir
                    o que a máquina entendeu é diferente de pedir de novo — que
                    gastaria um turno e produziria outra resposta. */}
                <button
                  type="button"
                  className="u1-ouvir"
                  onClick={() => void ouvirTexto('ouvido', ouvido)}
                  disabled={ouvido.trim() === '' || tocando !== null}
                >
                  {tocando === 'ouvido' ? '▮▮' : '▶'}
                  <span className="u1-oculto">Ouvir o que foi entendido</span>
                </button>
              </header>
              <p className="u1-texto" aria-live="polite">
                {parcial || ouvido || <span className="u1-vazio">Nada ainda.</span>}
              </p>
            </article>

            <article className="u1-bloco">
              <header className="u1-bloco__topo">
                <h2 className="u1-rotulo">Resposta</h2>
                <button
                  type="button"
                  className="u1-ouvir"
                  onClick={() => void ouvirTexto('resposta', resposta)}
                  disabled={resposta.trim() === '' || tocando !== null}
                >
                  {tocando === 'resposta' ? '▮▮' : '▶'}
                  <span className="u1-oculto">Ouvir a resposta de novo</span>
                </button>
              </header>
              <p className="u1-texto" aria-live="polite">
                {resposta !== '' ? (
                  resposta
                ) : pensando ? (
                  // Informação, não enfeite: o bloco ficava com um travessão
                  // parado enquanto o Will pensava, e "pensando" e "não vou
                  // responder" se pareciam. O texto oculto é o que o leitor de
                  // tela anuncia — três pontos animados não dizem nada a quem
                  // não os vê.
                  <span className="u1-pensando" role="status">
                    <i aria-hidden="true" />
                    <i aria-hidden="true" />
                    <i aria-hidden="true" />
                    <span className="u1-oculto">pensando</span>
                  </span>
                ) : (
                  <span className="u1-vazio">—</span>
                )}
              </p>
            </article>

            {aprovacao !== null && (
              <div className="u1-aprovacao" role="alertdialog" aria-live="assertive">
                <p className="u1-aprovacao-texto">
                  Preparei para <strong>{aprovacao.resumo}</strong>. Ainda não fiz nada.
                </p>
                {/* O que a pessoa precisa saber ANTES de clicar, e que o
                    servidor já mandava e ninguém mostrava: o nome exato do que
                    vai rodar, o risco classificado no registro, e que a decisão
                    tem prazo. O resumo diz o QUE; esta linha diz o QUANTO. */}
                {(aprovacao.ferramenta !== null ||
                  aprovacao.risco !== null ||
                  segundosParaVencer !== null) && (
                  <p className="u1-aprovacao-ficha">
                    {aprovacao.ferramenta !== null && (
                      <code className="u1-aprovacao-ferramenta">{aprovacao.ferramenta}</code>
                    )}
                    {aprovacao.risco !== null && (
                      <span
                        className={`u1-risco u1-risco--${aprovacao.risco}`}
                        title="risco classificado no registro de ferramentas"
                      >
                        risco {aprovacao.risco}
                      </span>
                    )}
                    {segundosParaVencer !== null && (
                      <span className="u1-aprovacao-prazo">
                        {segundosParaVencer > 0 ? (
                          <>
                            expira em {segundosParaVencer}s
                            {/* O número sozinho não diz o que acontece depois.
                                Para leitor de tela, o que importa é a
                                consequência, não a contagem. */}
                            <span className="u1-oculto">
                              ; depois disso será preciso pedir de novo
                            </span>
                          </>
                        ) : (
                          'prazo vencido — peça de novo'
                        )}
                      </span>
                    )}
                  </p>
                )}
                <div className="u1-aprovacao-botoes">
                  <button
                    type="button"
                    className="u1-botao u1-botao-sim"
                    onClick={() => decidirAprovacao(true)}
                  >
                    Confirmar
                  </button>
                  <button
                    type="button"
                    className="u1-botao"
                    onClick={() => decidirAprovacao(false)}
                  >
                    Agora não
                  </button>
                </div>
              </div>
            )}

            {erro !== null && (
              <p className="u1-erro" role="alert">
                {erro}{' '}
                <button type="button" className="u1-link" onClick={reiniciarSessao}>
                  reiniciar sessão
                </button>
              </p>
            )}

            <form className="u1-entrada" onSubmit={aoEnviarFormulario}>
              <label className="u1-oculto" htmlFor="u1-campo">
                Escreva para o Will
              </label>
              <input
                id="u1-campo"
                className="u1-campo"
                type="text"
                autoComplete="off"
                placeholder="Escreva para o Will…"
                value={rascunho}
                onChange={(e) => setRascunho(e.target.value)}
              />
              <button type="submit" className="u1-botao" disabled={rascunho.trim() === ''}>
                Enviar
              </button>
            </form>

            {/* A grade de ações da referência. Quatro, e só as que fazem algo
                agora: um botão que não faz nada em painel de comando é pior que
                a ausência dele, porque a pessoa aperta no momento em que mais
                precisa que funcione. */}
            <div className="u1-acoes">
              <button
                type="button"
                className={`u1-acao${microfone ? ' u1-acao--on' : ''}`}
                onClick={() => setMicrofone((ligado) => !ligado)}
                aria-pressed={microfone}
              >
                {microfone ? 'Parar de ouvir' : 'Ouvir'}
              </button>
              <button
                type="button"
                className="u1-acao"
                onClick={interromper}
                disabled={!conversa.falando}
              >
                Interromper
              </button>
              <button type="button" className="u1-acao" onClick={reiniciarSessao}>
                Nova sessão
              </button>
              <button
                type="button"
                className={`u1-acao${gaveta ? ' u1-acao--on' : ''}`}
                onClick={() => setGaveta(true)}
                aria-haspopup="dialog"
              >
                Configuração
              </button>
            </div>
          </div>
        </section>

        <aside className="u1-lado u1-lado--dir" aria-label="Conectores">
          <Conectores
            conectores={painel.conectores}
            ligados={conectoresLigados}
            onAlternar={alternarConector}
          />
        </aside>
      </main>

      <Gaveta aberta={gaveta} onFechar={() => setGaveta(false)} titulo="Configuração">
        <Panel mode={modoVoz} onModeChange={aoTrocarModo} conversationState={conversa.estado} />
      </Gaveta>
    </div>
  );
}
